import { getUserFriendlyError } from "@common/errorHandler";
import { BaseUrl } from "@config/api";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

interface UserParams {
  hospitalId: number;
  securityGroupId: string;
  firstName: string;
  lastName: string;
  emailID: string;
  countryCode: string;
  contactNumber: string;
  userTypeId: number;
  userId?: number;
  isActive?: boolean;
  zendeskSupportGroupId?: number;
  zendeskSupportGroup?: string;
  zendeskUserId?: number;
  zendeskOrgId?: number;
  isOnCallStatus?: boolean;
  statusChangeReason?: string;
  statusChangeComment?: string;
}

// Define the async thunk to add User
export const addUser = createAsyncThunk(
  "addUser",
  async (payload: UserParams, { rejectWithValue }) => {
    try {
      let url = `${BaseUrl}/api/user`;
      const response = await axios.post(url, payload);

      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message ?? "Failed to add user");
      }
    } catch (error: any) {
      return rejectWithValue(getUserFriendlyError(error));
    }
  }
);

export const updateUser = createAsyncThunk(
  "updateUser",
  async (payload: UserParams, { rejectWithValue }) => {
    try {
      let url = `${BaseUrl}/api/user`;
      const response = await axios.put(url, payload);
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(
          response.data.message ?? "Failed to update user"
        );
      }
    } catch (error: any) {
      return rejectWithValue(getUserFriendlyError(error));
    }
  }
);

// Define the async thunk to add User
export const getUserById = createAsyncThunk(
  "getUserById",
  async (arg: { userId: number }, { rejectWithValue }) => {
    try {
      let url = `${BaseUrl}/api/user/users/${arg.userId}`;
      const response = await axios.get(url);
      if (response.data) {
        return response.data.data;
      } else {
        return rejectWithValue(
          response.data.message ?? "Failed to fetch user details"
        );
      }
    } catch (error: any) {
      return rejectWithValue(getUserFriendlyError(error));
    }
  }
);

export const fetchCountryCodeList = createAsyncThunk(
  "fetchCountryCodeList",
  async (_, { rejectWithValue }) => {
    try {
      let url = `${BaseUrl}/api/auth/user/country`;
      const response = await axios.get(url);
      if (response.data) {
        return response.data.data;
      } else {
        return rejectWithValue(
          response.data.message ?? "Failed to fetch country code"
        );
      }
    } catch (error: any) {
      return rejectWithValue(getUserFriendlyError(error));
    }
  }
);

const initialState = {
  addUserloading: true,
  addUserError: undefined,
  updateUserloading: true,
  updateUserError: undefined,
  userDataLoading: false,
  userDataError: "",
  userDetails: {},
  countryCodeLoading: false,
  countryCodeError: "",
  countryCodeList: [],
};

const addUserSlice = createSlice({
  name: "addUser",
  initialState,
  reducers: {
    clearLoadingState: (state) => {
      state.addUserloading = true;
      state.updateUserloading = true;
      state.addUserError = undefined;
      state.updateUserError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUser.pending, (state) => {
        state.addUserloading = true;
        state.addUserError = undefined;
      })
      .addCase(addUser.fulfilled, (state) => {
        state.addUserloading = false;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.addUserloading = false;
        state.addUserError = action.payload as undefined;
      })

      .addCase(updateUser.pending, (state) => {
        state.updateUserloading = true;
        state.updateUserError = undefined;
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.updateUserloading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateUserloading = false;
        state.updateUserError = action.payload as undefined;
      })

      .addCase(getUserById.pending, (state) => {
        state.userDataLoading = true;
        state.userDataError = "";
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.userDataLoading = false;
        state.userDetails = action.payload.user;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.userDataLoading = false;
        state.userDataError = action.payload as string;
      })

      .addCase(fetchCountryCodeList.pending, (state) => {
        state.countryCodeLoading = true;
        state.countryCodeError = "";
      })
      .addCase(fetchCountryCodeList.fulfilled, (state, action) => {
        state.countryCodeLoading = false;
        state.countryCodeList = action.payload;
      })
      .addCase(fetchCountryCodeList.rejected, (state, action) => {
        state.countryCodeLoading = false;
        state.countryCodeError = action.payload as string;
      });
  },
});

export default addUserSlice.reducer;
export const { clearLoadingState } = addUserSlice.actions;
